import React from "react";
import { classes } from "@bloomberg/media-ui-core";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";
import Timestamp from "../Timestamp";
import styles from "./marketLastUpdate.module.css";
import assertNumericValue from "~utils/assertNumericValue";
import { TranslatedMessage } from "~utils/i18n";
import type { TransformEntry } from "~types/i18n";

export type MarketLastUpdateProps = {
  isOpen: boolean | null;
  isExchangeDelayed: boolean | null;
  lastUpdate: string | null;
  lastPostSessionPrice?: number;
  percentChange1DayPostRegular?: number;
  netChange1DayPostSessionRegular?: number;
  isRealTime: boolean;
  exchangeId: string;
};

function getExchangeDelayStatus(
  exchangeDelay: boolean | null,
  openStatus: boolean | null,
  isAfterHourPricing: boolean,
  isAfterHoursEnabled: string
): TransformEntry {
  if (
    exchangeDelay === false ||
    exchangeDelay === null ||
    ((openStatus === null || !openStatus) &&
      (!isAfterHourPricing || isAfterHoursEnabled === "false"))
  ) {
    return { id: "asOf", defaultMessage: "As of" };
  } else if (exchangeDelay) {
    return { id: "delayedPriceAsOf", defaultMessage: "Delayed price as of" };
  }

  return { id: "asOf", defaultMessage: "As of" };
}

const AFTER_HOURS_ENABLED =
  process.env.NEXT_PUBLIC_AFTER_HOURS_ENABLED || "false";

interface exchangeMap {
  [key: string]: string;
}

const exchangeIdMap: exchangeMap = {
  "39004": "Nasdaq",
};

export default function MarketLastUpdate(props: MarketLastUpdateProps) {
  const {
    isOpen,
    isExchangeDelayed,
    lastUpdate,
    lastPostSessionPrice,
    percentChange1DayPostRegular,
    netChange1DayPostSessionRegular,
    isRealTime,
    exchangeId,
  } = props;

  if (isOpen === null && !lastUpdate) {
    return null;
  }

  const afterHourPricing =
    assertNumericValue(lastPostSessionPrice) &&
    assertNumericValue(percentChange1DayPostRegular) &&
    assertNumericValue(netChange1DayPostSessionRegular);

  const { id, defaultMessage } = getExchangeDelayStatus(
    isExchangeDelayed,
    isOpen,
    afterHourPricing,
    AFTER_HOURS_ENABLED
  );

  return (
    <div
      className={classes([
        styles.marketStatus,
        typographyStyles.coreShortTextMd,
      ])}
    >
      {!(null === lastUpdate) && (
        <>
          <span className={styles.exchangeDelay}>
            <TranslatedMessage id={id} defaultMessage={defaultMessage} />
          </span>
          <Timestamp lastUpdate={lastUpdate} />
          <span className={styles.space}>.</span>
          {isRealTime &&
            isExchangeDelayed === false &&
            exchangeIdMap[exchangeId] && (
              <span className={styles.nasdaqSource}>
                {exchangeIdMap[exchangeId]}{" "}
                <TranslatedMessage
                  id={"lastSale"}
                  defaultMessage={"Last Sale"}
                />
              </span>
            )}
        </>
      )}
    </div>
  );
}
